import React, { useState } from 'react';

interface FloatingComplicProps {
  currentPage: string;
  onOpenChat: () => void;
  isListening?: boolean;
  isSpeaking?: boolean;
  isProcessing?: boolean;
  codePanelOpen?: boolean;
  chatPanelOpen?: boolean;
}

const FloatingComplic: React.FC<FloatingComplicProps> = ({
  onOpenChat,
  isListening = false,
  isSpeaking = false,
  isProcessing = false,
  codePanelOpen = false,
  chatPanelOpen = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getVoiceState = () => {
    if (isSpeaking) return 'speaking';
    if (isProcessing) return 'thinking';
    if (isListening) return 'listening';
    return 'idle';
  };

  const voiceState = getVoiceState();

  const getRingColor = () => {
    switch (voiceState) {
      case 'speaking': return 'border-green-400/50';
      case 'thinking': return 'border-yellow-400/50';
      case 'listening': return 'border-red-400/50';
      default: return 'border-gray-400/30 dark:border-gray-500/30';
    }
  };

  const getCoreColor = () => {
    switch (voiceState) {
      case 'speaking': return 'border-emerald-400/70';
      case 'thinking': return 'border-amber-400/70';
      case 'listening': return 'border-rose-400/70';
      default: return 'border-complic-400/60';
    }
  };

  const getGlowColor = () => {
    switch (voiceState) {
      case 'speaking': return 'from-emerald-400/40 via-emerald-300/20 to-teal-500/30';
      case 'thinking': return 'from-amber-400/40 via-amber-300/20 to-orange-400/30';
      case 'listening': return 'from-rose-400/40 via-rose-300/20 to-red-400/30';
      default: return 'from-complic-400/50 via-sky-400/25 to-complic-600/35';
    }
  };

  // Chat aside ≈ 36rem / max 40vw — sit just left of it
  const chatOffset = 'calc(min(36rem, 40vw) + 1.25rem)';

  const getPosition = (): React.CSSProperties => {
    if (chatPanelOpen) {
      return { right: chatOffset, left: 'auto', bottom: '2rem' };
    }
    if (codePanelOpen) {
      return { left: 'calc(66.666667% + 1rem)', right: 'auto', bottom: '2rem' };
    }
    return { right: '2rem', left: 'auto', bottom: '2rem' };
  };

  return (
    <div
      className="fixed z-50 cursor-pointer bottom-8 transition-all duration-300 ease-in-out"
      style={{
        transform: `scale(${isHovered ? 1.15 : 1}) rotate(${isHovered ? 5 : 0}deg)`,
        ...getPosition(),
      }}
      onClick={onOpenChat}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative flex items-center justify-center">
        <div className={`
          absolute w-32 h-32 rounded-full border transition-all duration-500
          ${getRingColor()}
          ${voiceState === 'speaking' ? 'animate-pulse-fast' : voiceState === 'listening' ? 'animate-pulse' : 'animate-pulse-slow'}
        `} />
        <div className={`
          absolute w-28 h-28 rounded-full border transition-all duration-500
          ${getRingColor()}
          ${voiceState === 'speaking' ? 'animate-pulse-fast' : voiceState === 'listening' ? 'animate-pulse' : 'animate-pulse-slow'}
        `} style={{ animationDelay: '0.5s' }} />
        <div className={`
          absolute w-24 h-24 rounded-full border transition-all duration-500
          ${getRingColor()}
          ${voiceState === 'speaking' ? 'animate-pulse-fast' : voiceState === 'listening' ? 'animate-pulse' : 'animate-pulse-slow'}
        `} style={{ animationDelay: '1s' }} />

        <div className={`
          relative rounded-full border-2 shadow-2xl
          flex items-center justify-center overflow-hidden
          transition-all duration-500 ease-out
          ${getCoreColor()}
          ${voiceState === 'speaking' ? 'animate-pulse-fast' : voiceState === 'listening' ? 'animate-pulse' : 'animate-pulse-slow'}
          w-20 h-20
        `}>
          <div className={`absolute inset-0 transition-all duration-500 bg-gradient-to-br ${getGlowColor()}`} />
          <div className={`
            absolute inset-0 bg-gradient-to-br transition-all duration-500
            ${voiceState === 'speaking' ? 'from-green-400/40 via-transparent to-green-600/40 animate-pulse-fast' :
              voiceState === 'listening' ? 'from-red-400/40 via-transparent to-red-600/40 animate-pulse' :
              voiceState === 'thinking' ? 'from-amber-400/40 via-transparent to-amber-500/40' :
              'from-complic-400/35 via-transparent to-sky-400/30'}
          `} />
          <span className="relative z-10 text-2xl font-bold text-white drop-shadow-lg select-none">
            C
          </span>
        </div>
      </div>
    </div>
  );
};

export default FloatingComplic;
